/**
 * Thème clair / sombre — le pendant de `i18n.ts` pour l'apparence.
 *
 * Les jetons des deux thèmes existent depuis L10a : `[data-theme='sombre']` ne redéfinit que la
 * couche des RÔLES, aucun composant n'est touché. Il ne manquait que ce module — le réglage, sa
 * mémoire, et l'écoute du système.
 *
 * **Trois états et non deux.** « Clair », « Sombre », et surtout **« comme le système »**, qui est
 * le défaut. Un produit qui force un thème contredit un réglage que l'utilisateur a déjà pris au
 * niveau de son téléphone ; et une bascule à deux états ne permet plus d'y revenir une fois qu'on
 * en est sorti. Le troisième état n'est pas un luxe, c'est le seul moyen de rendre le réglage
 * réversible.
 *
 * **Pourquoi l'attribut est posé avant le premier rendu** (`main.tsx`) : une page qui s'affiche en
 * clair puis bascule en sombre produit un éclair blanc dans le noir. C'est le même raisonnement
 * que pour `dir` en RTL — un basculement visible après coup se lit comme un défaut.
 */

export type Theme = 'clair' | 'sombre' | 'systeme'

export const THEMES: readonly Theme[] = ['systeme', 'clair', 'sombre'] as const

const CLE = 'coloc.theme'

function estUnTheme(valeur: string | null): valeur is Theme {
  return valeur !== null && (THEMES as readonly string[]).includes(valeur)
}

/** Le réglage enregistré. « comme le système » tant que personne n'a choisi. */
export function themeInitial(): Theme {
  try {
    const enregistre = localStorage.getItem(CLE)

    return estUnTheme(enregistre) ? enregistre : 'systeme'
  } catch {
    // Navigation privée, stockage refusé : le réglage par défaut reste utilisable.
    return 'systeme'
  }
}

/** Ce que le système préfère, à cet instant. */
export function themeDuSysteme(): 'clair' | 'sombre' {
  return globalThis.matchMedia?.('(prefers-color-scheme: dark)').matches === true
    ? 'sombre'
    : 'clair'
}

/** Le thème réellement peint, une fois « comme le système » résolu. */
export function themeEffectif(theme: Theme): 'clair' | 'sombre' {
  return theme === 'systeme' ? themeDuSysteme() : theme
}

/**
 * Applique le thème au document.
 *
 * L'attribut n'est posé **que** pour le sombre : le clair est ce que `:root` porte déjà, et un
 * `data-theme='clair'` obligerait à écrire deux fois chaque jeton. C'est aussi ce qui fait que le
 * thème par défaut ne dépend d'aucun script — une page rendue sans JavaScript reste lisible.
 *
 * `color-scheme` n'est pas décoratif : c'est lui qui fait basculer les contrôles natifs — barres de
 * défilement, sélecteurs de date, champs `<input>`. Sans lui, un calendrier blanc s'ouvre au milieu
 * d'une interface sombre.
 */
export function appliquerTheme(theme: Theme): void {
  const effectif = themeEffectif(theme)
  const racine = document.documentElement

  if (effectif === 'sombre') {
    racine.setAttribute('data-theme', 'sombre')
  } else {
    racine.removeAttribute('data-theme')
  }

  racine.style.colorScheme = effectif === 'sombre' ? 'dark' : 'light'

  // La couleur de la barre d'adresse mobile suit le thème. Sans cela, une barre claire surmonte
  // une application sombre, et l'installation PWA se remarque par ce défaut-là.
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', effectif === 'sombre' ? '#100f0d' : '#1d4e4f')
}

export function enregistrerTheme(theme: Theme): void {
  try {
    localStorage.setItem(CLE, theme)
  } catch {
    /* le réglage ne survivra pas à l'onglet — ce n'est pas une raison de ne pas l'appliquer */
  }
}

/**
 * S'abonne aux changements de préférence du système.
 *
 * N'a d'effet qu'en mode « comme le système », mais l'abonnement est posé dans tous les cas :
 * l'utilisateur peut revenir à ce mode sans recharger la page, et il faut alors que l'écoute soit
 * déjà en place.
 */
export function ecouterLeSysteme(rappel: () => void): () => void {
  const requete = globalThis.matchMedia?.('(prefers-color-scheme: dark)')

  if (requete === undefined) {
    return () => undefined
  }

  requete.addEventListener('change', rappel)

  return () => requete.removeEventListener('change', rappel)
}
